// ── Shared building blocks for the General Rules V3 page ─────────────────────
// Every size is Figma px / 8 in rem (1rem = 8px at the design width).
// Desktop frames are 1920 wide, mobile frames 430 wide; the `lg` breakpoint is 993px.

import type { ReactNode } from 'react';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import type { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import { RuleIcon, ArrowRightIcon, CheckmarkIcon, CloseIcon } from './Icons';
import type {
  RulesBreachStep,
  RulesProductCard,
  RulesStatCard,
  RulesTableRow,
  RulesTimeCard,
  RulesValueTone,
} from '@/sanity/pages/PageGeneralRulesV3Template';

// ── Rich text ─────────────────────────────────────────────────────────────────
// Body copy is gray; the editor's bold marks become the black medium words.

export function makeRichComponents(strongClassName: string): PortableTextComponents {
  return {
    block: {
      normal: ({ children }) => <p className="mb-0">{children}</p>,
      h2: ({ children }) => <p className="mb-0">{children}</p>,
      h3: ({ children }) => <p className="mb-0">{children}</p>,
      h4: ({ children }) => <p className="mb-0">{children}</p>,
    },
    list: {
      bullet: ({ children }) => <ul className="mb-0 list-disc pl-[2.5rem]">{children}</ul>,
      number: ({ children }) => <ol className="mb-0 list-decimal pl-[2.5rem]">{children}</ol>,
    },
    listItem: {
      bullet: ({ children }) => <li className="mb-0">{children}</li>,
      number: ({ children }) => <li className="mb-0">{children}</li>,
    },
    marks: {
      strong: ({ children }) => <strong className={cn('font-medium', strongClassName)}>{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      underline: ({ children }) => <span className="underline">{children}</span>,
      color: ({ children, value }) => (
        <span className="font-[inherit]" style={{ color: (value as { hex?: string })?.hex }}>
          {children}
        </span>
      ),
      link: ({ children, value }) => (
        <a href={(value as { href?: string })?.href} className="underline">
          {children}
        </a>
      ),
    },
  };
}

export const bodyRich = makeRichComponents('text-black');
export const greenRich = makeRichComponents('text-[#196551]');
export const amberRich = makeRichComponents('text-[#7a4504]');
export const mutedRich = makeRichComponents('text-black font-normal');

export function Rich({
  value,
  components = bodyRich,
  className,
  style,
}: {
  value?: PortableTextBlock[];
  components?: PortableTextComponents;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (!value?.length) return null;
  return (
    <div className={className} style={style}>
      <PortableText value={value} components={components} />
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

/** Gray rounded "Box" that groups the blocks of one topic. */
export function RuleBox({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<`data-${string}`, string>) {
  return (
    <div
      {...rest}
      className={cn(
        'flex w-full flex-col gap-[3rem] rounded-[0.5rem] bg-[#f2f4f3]/50 p-[2rem] lg:p-[3rem]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** White card that frames an example, chart or simulator. */
export function FigureFrame({
  children,
  className,
  padding = 'p-[3rem]',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  padding?: string;
} & Record<`data-${string}`, string>) {
  return (
    <div {...rest} className={cn('flex w-full flex-col gap-[3rem] rounded-[0.25rem] bg-white', padding, className)}>
      {children}
    </div>
  );
}

/** "EXAMPLE · $50,000 ACCOUNT" eyebrow. */
export function ExampleLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('mb-0 font-geist text-[2rem] font-medium uppercase leading-[3rem] text-black/40', className)}>
      {children}
    </p>
  );
}

// ── Text block ────────────────────────────────────────────────────────────────

export function TextBlock({
  heading,
  body,
  bodyWidth,
  size = 'body',
}: {
  heading?: string;
  body?: PortableTextBlock[];
  /** Figma px; caps the body on desktop so it wraps like the design. */
  bodyWidth?: number;
  size?: 'body' | 'small';
}) {
  if (!heading && !body?.length) return null;
  return (
    <div className="flex w-full flex-col gap-[1.5rem]">
      {heading && (
        // A "\n" in the heading breaks the line on mobile only (Figma mobile headings wrap by hand).
        <p className="mb-0 whitespace-pre-line font-geist text-[3rem] font-medium leading-[4rem] text-black lg:whitespace-normal">
          {heading}
        </p>
      )}
      <Rich
        value={body}
        components={size === 'small' ? mutedRich : bodyRich}
        style={bodyWidth ? ({ '--body-w': `${bodyWidth / 8}rem` } as React.CSSProperties) : undefined}
        className={cn(
          'flex flex-col gap-[1.5rem] font-geist',
          bodyWidth && 'lg:max-w-[var(--body-w)]',
          size === 'small'
            ? 'text-[2rem] leading-[3rem] text-black/40'
            : 'text-[2.25rem] leading-[3.5rem] text-black/[0.64]',
        )}
      />
    </div>
  );
}

// ── Callout ───────────────────────────────────────────────────────────────────

export function Callout({
  tone = 'green',
  icon = 'arrow-right',
  title,
  body,
}: {
  tone?: 'green' | 'amber';
  icon?: string;
  title?: string;
  body?: PortableTextBlock[];
}) {
  const amber = tone === 'amber';
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-[1.5rem] rounded-[0.25rem] px-[2.5rem] py-[2rem] lg:flex-row lg:gap-[2rem]',
        amber ? 'bg-[#fdf3e2]' : 'bg-[#e2f3ef]',
        title ? 'lg:items-start' : 'lg:items-center',
      )}
    >
      <div
        className={cn(
          // Some Figma mobile frames hug the glyph (32×16); the 32×32 box is the designed state.
          'flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-[0.25rem] text-white',
          amber ? 'bg-[#a15c07]' : 'bg-[#228e71]',
        )}
      >
        <RuleIcon type={icon} className="h-[2rem] w-[2rem]" />
      </div>
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-[0.5rem] font-geist text-[2rem] leading-[3rem]',
          amber ? 'text-[#7a4504]' : 'text-[#196551]',
        )}
      >
        {title && <p className="mb-0 font-medium">{title}</p>}
        <Rich
          value={body}
          components={amber ? amberRich : greenRich}
          className={cn(
            'flex flex-col gap-[1.5rem]',
            amber ? 'text-[#7a4504]/[0.88]' : 'text-[#196551]/[0.88]',
            !title && 'text-[#196551]',
          )}
        />
      </div>
    </div>
  );
}

// ── Stat cards (Evaluation 8% × Funding Dynamic) ──────────────────────────────

export function StatCards({ cards = [] }: { cards?: RulesStatCard[] }) {
  if (!cards.length) return null;
  return (
    <div className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-stretch">
      {cards.map((card, i) => {
        const neutral = card.badgeTone === 'neutral';
        return (
          <div
            key={card._key ?? i}
            className="flex min-w-0 flex-1 flex-col gap-[1.5rem] rounded-[0.5rem] bg-white p-[3rem]"
          >
            {card.badge && (
              <div
                className={cn(
                  'inline-flex w-fit items-center gap-[0.75rem] rounded-[0.5rem] px-[1.25rem] py-[0.5rem]',
                  neutral ? 'bg-black/[0.04]' : 'bg-[#1aa87f]/[0.08]',
                )}
              >
                <span
                  className={cn('h-[0.75rem] w-[0.75rem] shrink-0 rounded-full', neutral ? 'bg-black/40' : 'bg-[#228e71]')}
                />
                <span
                  className={cn(
                    'font-geist text-[1.75rem] font-semibold uppercase leading-[2.25rem]',
                    neutral ? 'text-black/70' : 'text-[#228e71]',
                  )}
                >
                  {card.badge}
                </span>
              </div>
            )}
            <div className="flex w-full flex-col justify-end gap-[1.75rem]">
              {card.value && (
                <p className="mb-0 font-geist text-[4rem] font-normal leading-[5rem] tracking-[-0.12rem] text-black [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                  {card.value}
                </p>
              )}
              <Rich
                value={card.description}
                components={mutedRich}
                className="font-geist text-[2rem] leading-[3rem] tracking-[-0.06rem] text-black/40 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Breach sequence (First violation → Second violation) ──────────────────────

const BREACH_TONES: Record<string, { card: string; label: string; title: string }> = {
  green: { card: 'bg-[#e2f3ef]', label: 'text-[#196551]/80', title: 'text-black' },
  red: { card: 'bg-[#ac0003]/[0.08]', label: 'text-[#ac0003]/80', title: 'text-[#ac0003]' },
  neutral: { card: 'bg-white', label: 'text-black/80', title: 'text-black/40' },
};

export function BreachSequence({ steps = [] }: { steps?: RulesBreachStep[] }) {
  if (!steps.length) return null;
  return (
    <div className="flex w-full flex-col items-stretch gap-[1rem] lg:flex-row lg:items-stretch">
      {steps.map((step, i) => {
        const tone = BREACH_TONES[step.tone ?? 'green'] ?? BREACH_TONES.green;
        const lines = (step.lines ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
        return (
          <div key={step._key ?? i} className="contents">
            {i > 0 && (
              <div className="flex shrink-0 items-center justify-center self-center text-black">
                <ArrowRightIcon className="h-[2rem] w-[2rem] rotate-90 lg:rotate-0" />
              </div>
            )}
            <div
              className={cn(
                'flex min-w-0 flex-1 flex-col gap-[1rem] rounded-[0.25rem] p-[2rem] font-geist',
                tone.card,
              )}
            >
              {step.label && (
                <p className={cn('mb-0 text-[2rem] font-medium uppercase leading-[3rem]', tone.label)}>
                  {step.label}
                </p>
              )}
              {step.title && (
                <p className={cn('mb-0 text-[2.5rem] font-medium leading-[3.5rem]', tone.title)}>{step.title}</p>
              )}
              {lines.length > 0 && (
                <div className="text-[2rem] leading-[3rem] text-black/[0.64]">
                  {lines.map((line, j) => (
                    <p key={j} className="mb-0">
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Time cards (reset clocks) ─────────────────────────────────────────────────

export function TimeCards({ cards = [] }: { cards?: RulesTimeCard[] }) {
  if (!cards.length) return null;
  return (
    <div className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-stretch">
      {cards.map((card, i) => (
        <div
          key={card._key ?? i}
          className="flex min-w-0 flex-1 items-center gap-[2rem] rounded-[0.25rem] bg-white px-[2.5rem] py-[2rem] font-geist lg:h-[16.75rem]"
        >
          <div className="h-[2.5rem] w-[2.5rem] shrink-0 text-black/[0.64]">
            <RuleIcon type="time" />
          </div>
          <div className="flex flex-col gap-[0.25rem] whitespace-nowrap">
            {card.label && <p className="mb-0 text-[2rem] leading-[3rem] text-black/[0.64]">{card.label}</p>}
            {card.value && (
              <p className="mb-0 text-[4rem] font-medium leading-[5rem] text-black">{card.value}</p>
            )}
          </div>
          <div className="h-px min-w-0 flex-1" />
          {card.suffix && (
            <p className="mb-0 whitespace-nowrap text-[2rem] leading-[3rem] text-black/40">{card.suffix}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Product cards (Futures × Crypto & CFD week view) ──────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function StatusPill({
  tone = 'green',
  children,
  className,
}: {
  tone?: 'green' | 'red' | 'amber';
  children: ReactNode;
  className?: string;
}) {
  const styles = {
    green: 'bg-[#e2f3ef] text-[#196551]',
    red: 'bg-[#ac0003]/[0.08] text-[#ac0003]',
    amber: 'bg-[#fdf3e2] text-[#7a4504]',
  }[tone];
  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center gap-[0.75rem] rounded-[0.25rem] px-[1.25rem] py-[0.25rem] font-geist text-[2rem] font-medium leading-[3rem]',
        styles,
        className,
      )}
    >
      <span className="h-[1.75rem] w-[1.75rem] shrink-0">{tone === 'red' ? <CloseIcon /> : <CheckmarkIcon />}</span>
      <span className="whitespace-nowrap">{children}</span>
    </div>
  );
}

export function ProductCards({ cards = [] }: { cards?: RulesProductCard[] }) {
  if (!cards.length) return null;
  return (
    <div className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-stretch">
      {cards.map((card, i) => {
        const days = (card.days ?? '1111100').padEnd(7, '0').slice(0, 7).split('');
        return (
          <div
            key={card._key ?? i}
            className="flex min-w-0 flex-1 flex-col gap-[2rem] rounded-[0.25rem] bg-white p-[3rem] font-geist"
          >
            <div className="flex w-full items-center justify-between gap-[1rem]">
              {card.title && (
                <p className="mb-0 text-[2.5rem] font-medium leading-[3.5rem] text-black">{card.title}</p>
              )}
              {card.status && <StatusPill tone={card.statusTone ?? 'green'}>{card.status}</StatusPill>}
            </div>
            <div className="flex w-full gap-[0.5rem]">
              {days.map((d, j) => (
                <div key={j} className="flex min-w-0 flex-1 flex-col items-center gap-[0.75rem] lg:items-start">
                  <div
                    className={cn(
                      'h-[1rem] w-full rounded-[0.25rem]',
                      d === '1' ? 'bg-[#228e71]' : d === '2' ? 'bg-[#228e71]/50' : 'bg-black/[0.08]',
                    )}
                  />
                  <p className="mb-0 text-[2rem] leading-[3rem] text-black/40">{DAY_LABELS[j]}</p>
                </div>
              ))}
            </div>
            <Rich
              value={card.body}
              className="flex flex-col gap-[1.5rem] text-[2rem] leading-[3rem] text-black/[0.64]"
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Key / value table ─────────────────────────────────────────────────────────

const VALUE_TONE_CLASS: Record<RulesValueTone, string> = {
  default: 'text-black',
  green: 'text-[#228e71]',
  red: 'text-[#ac0003]',
  muted: 'text-black/40',
};

export function KeyValueTable({
  label,
  rows = [],
  note,
  className,
}: {
  label?: string;
  rows?: RulesTableRow[];
  note?: PortableTextBlock[];
  className?: string;
}) {
  if (!label && !rows.length && !note?.length) return null;
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-[1rem] rounded-[0.25rem] bg-[#f2f4f3] px-[2.5rem] py-[2rem] font-geist',
        className,
      )}
    >
      {label && <ExampleLabel>{label}</ExampleLabel>}
      {rows.map((row, i) => (
        <div
          key={row._key ?? i}
          className="flex w-full items-center justify-between gap-[2rem] border-b border-black/[0.06] py-[1.25rem] text-[2rem] leading-[3rem]"
        >
          <p className="mb-0 text-black/[0.64]">{row.label}</p>
          <p className={cn('mb-0 shrink-0 font-medium', VALUE_TONE_CLASS[row.valueTone ?? 'default'])}>
            {row.value}
          </p>
        </div>
      ))}
      <Rich
        value={note}
        className="flex flex-col gap-[1rem] pb-[1rem] pt-[1.5rem] text-[2rem] leading-[3rem] text-black/[0.64]"
      />
    </div>
  );
}

// ── Numbered steps (white card) ───────────────────────────────────────────────

export function Steps({
  title,
  icon = 'timer',
  steps = [],
  tableLabel,
  tableRows,
  tableNote,
}: {
  title?: string;
  icon?: string;
  steps?: { _key?: string; title?: string; body?: PortableTextBlock[] }[];
  tableLabel?: string;
  tableRows?: RulesTableRow[];
  tableNote?: PortableTextBlock[];
}) {
  return (
    <FigureFrame padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem]">
      {title && (
        <div className="flex items-center gap-[2rem]">
          <div className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center text-[#228e71]">
            <RuleIcon type={icon} className="h-[2.5rem] w-[2.5rem]" />
          </div>
          <p className="mb-0 font-geist text-[2.5rem] font-medium leading-[3.5rem] text-black">{title}</p>
        </div>
      )}
      {steps.length > 0 && (
        <div className="flex w-full flex-col gap-[2rem]">
          {steps.map((step, i) => (
            <div key={step._key ?? i} className="flex w-full items-start gap-[2rem]">
              <div className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-[0.25rem] border border-black/10 bg-white font-geist text-[2rem] font-medium leading-[3rem] text-black">
                {i + 1}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-[0.5rem] font-geist text-[2rem] leading-[3rem]">
                {step.title && <p className="mb-0 font-medium text-black">{step.title}</p>}
                <Rich value={step.body} className="flex flex-col gap-[1rem] text-black/[0.64]" />
              </div>
            </div>
          ))}
        </div>
      )}
      <KeyValueTable label={tableLabel} rows={tableRows} note={tableNote} />
    </FigureFrame>
  );
}

// ── Applies-to chip ───────────────────────────────────────────────────────────

export function AppliesChip({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-[0.75rem] rounded-[0.25rem] bg-[#e2f3ef] px-[1.25rem] py-[0.25rem]">
      <span className="h-[0.75rem] w-[0.75rem] shrink-0 rounded-full bg-[#228e71]" />
      <span className="whitespace-nowrap font-geist text-[2rem] font-medium leading-[3rem] text-[#196551]">
        {children}
      </span>
    </div>
  );
}
